import React, { useRef, useState } from "react";
import { IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButton, IonGrid, IonRow, IonCol, IonImg, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

// List of Good and Bad Habits
const habits = [
    { type: "good", name: "Brushing Teeth", image: "brushing-teeth.png" },
    { type: "bad", name: "Eating Too Many Candies", image: "eating-candies.png" },
    { type: "good", name: "Washing Hands", image: "washing-hands.png" },
    { type: "bad", name: "Not Washing Hands", image: "not-washing-hands.png" },
    { type: "good", name: "Helping Others", image: "helping-others.png" },
    { type: "bad", name: "Being Mean", image: "being-mean.png" },
    { type: "good", name: "Eating Vegetables", image: "eating-vegetables.png" },
    { type: "bad", name: "Throwing Trash on Road", image: "throwing-trash.png" },
    { type: "good", name: "Sleeping Early", image: "sleeping-early.png" },
    { type: "bad", name: "Watching Too Much TV", image: "watching-tv.png" },
    { type: "bad", name: "Eating Too Many Junk Food", image: "too-many-junk-foods.png" },
    { type: "bad", name: "Not Sharing with Friends", image: "not-sharing.png" },
    { type: "good", name: "Sharing with Friends", image: "sharewithfriend.png" },
];

// Function to get a new habit pair (one good & one bad)
const getRandomHabitPair = () => {
    document.title = "Choose the Correct Habit – Nursery Worksheet | Mama’s Guidance"; // Set page title
    const goodHabits = habits.filter(habit => habit.type === "good");
    const badHabits = habits.filter(habit => habit.type === "bad");

    const goodHabit = goodHabits[Math.floor(Math.random() * goodHabits.length)];
    const badHabit = badHabits[Math.floor(Math.random() * badHabits.length)];

    return Math.random() > 0.5 ? [goodHabit, badHabit] : [badHabit, goodHabit];
};

const GoodHabitGame: React.FC = () => {
    const [habitPair, setHabitPair] = useState(getRandomHabitPair());
    const [feedback, setFeedback] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Handle selection
    const handleSelection = (selected: string) => {
        if (selected === "good") {
            setShowImage(true);
            setTimeout(() => {
                setHabitPair(getRandomHabitPair());
                setFeedback("");
                setShowImage(false);
            }, 5000)
        } else {
            setFeedback("❌ Oops! Try Again.");
        }
    };

    // Load a new habit pair
    const nextQuestion = () => {
        setHabitPair(getRandomHabitPair());
        setFeedback("");
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Help nursery kids learn about good and bad habits with the "Choose the Correct Habit" activity on Mamas Guidance—an interactive exercise that encourages children to identify and choose healthy habits, fostering positive behavior and self-awareness.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord matchImage match-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Choose the Correct Habit</h1>

                                    <p>
                                        Let’s help little ones learn the difference between right and wrong with this <strong>Choose the Correct Habit</strong> worksheet! Through pictures and simple examples, nursery children will identify <strong>good habits and avoid bad ones</strong>, building a strong foundation in values and hygiene.
                                    </p>

                                    <h2>🧼 What’s in the Worksheet?</h2>
                                    <p>
                                        The worksheet shows picture pairs or actions such as:
                                        <ul>
                                            <li>Brushing teeth ✅ vs. Eating too many candies ❌</li>
                                            <li>Throwing garbage in the bin ✅ vs. Littering the floor ❌</li>
                                            <li>Helping others ✅ vs. Fighting with friends ❌</li>
                                        </ul>
                                        Children must <strong>circle or choose the good habit</strong> in each pair.
                                    </p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Recognize common good and bad habits</li>
                                        <li>Promote personal hygiene and manners</li>
                                        <li>Develop basic moral values and responsibility</li>
                                        <li>Improve decision-making through visual clues</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use the Worksheet</h2>
                                    <ul>
                                        <li>Show your child the two options.</li>
                                        <li>Discuss what’s happening in each image.</li>
                                        <li>Ask them to choose which action is the good habit.</li>
                                        <li>Reinforce with real-life examples and stories.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/nursery/general-awareness/neighbourhood-area/guess-the-area"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Guess the Area</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/general-awareness/colors/pick-color"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Pick Color</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Guess Community Helper</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why This Activity Is Important</h2>
                                    <p>
                                        Teaching good habits at an early age lays the foundation for strong character. This worksheet encourages awareness of <strong>self-care, respect, responsibility, and kindness</strong>.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Choose the Correct Habit</h1>

                                <p>
                                    Let’s help little ones learn the difference between right and wrong with this <strong>Choose the Correct Habit</strong> worksheet! Through pictures and simple examples, nursery children will identify <strong>good habits and avoid bad ones</strong>, building a strong foundation in values and hygiene.
                                </p>

                                <h2>🧼 What’s in the Worksheet?</h2>
                                <p>
                                    The worksheet shows picture pairs or actions such as:
                                    <ul>
                                        <li>Brushing teeth ✅ vs. Eating too many candies ❌</li>
                                        <li>Throwing garbage in the bin ✅ vs. Littering the floor ❌</li>
                                        <li>Helping others ✅ vs. Fighting with friends ❌</li>
                                    </ul>
                                    Children must <strong>circle or choose the good habit</strong> in each pair.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Recognize common good and bad habits</li>
                                    <li>Promote personal hygiene and manners</li>
                                    <li>Develop basic moral values and responsibility</li>
                                    <li>Improve decision-making through visual clues</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Show your child the two options.</li>
                                    <li>Discuss what’s happening in each image.</li>
                                    <li>Ask them to choose which action is the good habit.</li>
                                    <li>Reinforce with real-life examples and stories.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/general-awareness/neighbourhood-area/guess-the-area"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Guess the Area</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/general-awareness/colors/pick-color"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Pick Color</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Guess Community Helper</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Activity Is Important</h2>
                                <p>
                                    Teaching good habits at an early age lays the foundation for strong character. This worksheet encourages awareness of <strong>self-care, respect, responsibility, and kindness</strong>.
                                </p>
                            </article>
                            <IonGrid>
                                <IonRow className="ion-justify-content-center">
                                    {habitPair.map((habit, index) => (
                                        <IonCol key={index} size="12" className="ion-text-center">
                                            <img
                                                src={process.env.REACT_APP_API_URL + habit.image}
                                                alt={habit.name}
                                                className="habit-image"
                                                onClick={() => handleSelection(habit.type)}
                                                style={{ width: "100px", height: "100px", cursor: "pointer" }}
                                            />
                                            <p>{habit.name}</p>
                                        </IonCol>
                                    ))}
                                </IonRow>
                            </IonGrid>
                            <h3 className="ion-text-center">{feedback}</h3>
                            {/* <div className="ion-text-center">
                            <div className="btn hero-btn" onClick={nextQuestion}>Next Question</div>
                        </div> */}
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default GoodHabitGame;
